import React, { useState, useContext, useEffect } from 'react';
import styled from 'styled-components';

import { getAvatarURL } from 'utils/cloudinary';
import { EVENTS } from 'constants/events';
import { logEvent } from 'utils/amplitude';
import * as AuthUtils from 'utils/auth';
import { allowMarketplace } from 'utils/employee';
import {
  RootRoutes,
  HistoryRoutes,
  ClaimRoutes,
  StoreRoutes,
  AccountRoutes,
  SettingsRoutes,
  SupportRoutes,
} from 'utils/routeGenerator';

import FeatureFlagContext from 'contexts/FeatureFlagContext';

import {
  SvgIconSize,
  MenuHorizontalAlignment,
} from '@joinforma/ui/constants/properties';
import { ScreenWidth } from '@joinforma/ui/constants/breakpoints';
import { FeatureFlag } from 'constants/featureFlag';

import { ReactComponent as SettingSvg } from '@joinforma/ui/assets/icons/settings.svg';
import { ReactComponent as TimerSvg } from '@joinforma/ui/assets/icons/timer.svg';
import { ReactComponent as DocumentSvg } from '@joinforma/ui/assets/icons/document.svg';
import { ReactComponent as DocumentsSvg } from '@joinforma/ui/assets/icons/documents.svg';
import { ReactComponent as LogOutSvg } from '@joinforma/ui/assets/icons/exit.svg';
import { ReactComponent as CaretDownSvg } from '@joinforma/ui/assets/icons/caret-down.svg';
import { ReactComponent as StoreSvg } from '@joinforma/ui/assets/icons/store.svg';
import { ReactComponent as WalletSvg } from '@joinforma/ui/assets/icons/wallet.svg';
import { ReactComponent as FloatSvg } from 'assets/images/float.svg';

import useSnackbar from '@joinforma/ui/hooks/useSnackbar';
import Switch from '@joinforma/ui/components/Switch';

import Menu from '@joinforma/ui/components/Menu';
import MenuLabel from '@joinforma/ui/components/MenuLabel';
import MenuItem from '@joinforma/ui/components/MenuItem';
import SvgIcon from '@joinforma/ui/components/SvgIcon';
import { Company, Employee } from './types';
import Sprig from 'utils/sprig';

const DownArrowIcon = styled(SvgIcon).attrs({
  icon: CaretDownSvg,
  size: SvgIconSize.SMALL,
})``;

const ProfilePic = styled.img`
  margin-right: 5px;
  width: 40px;
  height: 40px;
  border-radius: 20px;
  object-fit: contain;
`;

const Container = styled.div`
  cursor: pointer;
  height: 100%;
  display: flex;
  align-items: center;
  position: relative;
`;

const AvatarContainer = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  height: 100%;
`;

const Divider = styled.div`
  height: 1px;
  background-color: ${({
    theme: {
      colors: {
        background: { stroke },
      },
    },
  }) => stroke};
  width: 100%;
  margin: 12px 0;

  @media (min-width: ${ScreenWidth.LAPTOP}) {
    display: none;
  }
`;

const MobileMenuLabel = styled(MenuLabel)`
  @media (min-width: ${ScreenWidth.LAPTOP}) {
    display: none;
  }
`;

const MobileMenuItem = styled(MenuItem)`
  @media (min-width: ${ScreenWidth.LAPTOP}) {
    display: none;
  }
`;

const ToggleCookie = styled(Switch)`
  margin-left: 8px;

  @media (min-width: ${ScreenWidth.DESKTOP}) {
    display: flex;
  }
`;

const TopNavUser = ({
  company,
  employee,
  pathname,
  hasPostTaxWallets,
  show_store,
  show_claims,
}: {
  company: Company;
  employee: Employee;
  pathname: string;
  hasPostTaxWallets: boolean;
  show_store?: boolean;
  show_claims?: boolean;
}) => {
  const [is_open, setIsOpen] = useState(false);
  const { reimbursement_policy_link } = company.settings;
  const { showPlainSnackbar } = useSnackbar();

  const feature_flag = useContext(FeatureFlagContext);

  const sprigUserResearchEnabled = feature_flag.flag(
    FeatureFlag.SPRIG_USER_RESEARCH,
    false
  );

  const doLogOut = () => {
    if (sprigUserResearchEnabled) {
      Sprig.logoutUser();
    }
    showPlainSnackbar({ text: 'Logging out...' });
    AuthUtils.removeAuthCookie();
    AuthUtils.removeEmployeeIdCookie();
    AuthUtils.removeRedirectCookie();
    window.location.href = RootRoutes.login();
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      setIsOpen(false);
    }
  };

  const handlePretaxToggle = () => {
    localStorage.setItem('employeeId', employee.id);

    if (localStorage.getItem('pretax_v2')) {
      localStorage.removeItem('pretax_v2');
      window.location.reload();
    } else {
      localStorage.setItem('pretax_v2', 'true');
      window.location.reload();
    }
  };

  const avatar =
    employee.avatar_url ||
    'https://s3-us-west-1.amazonaws.com/twic.ai/avatar/default_avatar.png';

  const showExperimentToggle: boolean = feature_flag.flag(
    FeatureFlag.PRETAX_EXPERIMENT_TOGGLE,
    false
  );

  const pretaxV2Enabled: boolean = feature_flag.flag(
    FeatureFlag.FORMA_PRETAX_ACCOUNTS_CLAIMS_PAGE,
    false
  );

  /**
   * This useEffect sets the default value of the Pretax 2.0 toggle
   * based on if pretax 2.0 is enabled for the customer.
   */
  useEffect(() => {
    const pretaxV2LocalStorage = localStorage.getItem('pretax_v2');
    const memberIdInLocalStorage = localStorage.getItem('employeeId');
    const currentMemberId = employee.id;

    /**
     * This allows the effect to run for every member account the ISP user sees but prevents the effect from running
     * if ISP user manually toggles the switch. Without this, if ISP user toggles the switch, the effect would re-run to
     * undo that change since a Member can ONLY be Pretax2.0 or NOT.
     */
    if (memberIdInLocalStorage === currentMemberId) {
      return;
    }

    if (pretaxV2Enabled && !pretaxV2LocalStorage) {
      localStorage.setItem('pretax_v2', 'true');
      window.location.reload();
    }

    if (!pretaxV2Enabled && pretaxV2LocalStorage) {
      localStorage.removeItem('pretax_v2');
      window.location.reload();
    }
  }, [pretaxV2Enabled, employee.id]);

  return (
    <Container>
      {showExperimentToggle && (
        <ToggleCookie
          checked={!!localStorage.getItem('pretax_v2')}
          onChange={() => handlePretaxToggle()}
        >
          Pretax 2.0
        </ToggleCookie>
      )}
      <AvatarContainer
        onClick={() => setIsOpen((current_is_open) => !current_is_open)}
        onKeyDown={handleKeyPress}
        aria-haspopup="true"
        aria-expanded={is_open ? 'true' : 'false'}
        aria-controls="user-menu"
        id="user-menu-button"
      >
        <ProfilePic
          src={getAvatarURL(avatar, { width: 40, height: 40 })}
          alt="Profile Avatar"
        />
        <DownArrowIcon />
      </AvatarContainer>
      <Menu
        id="user-menu"
        aria-labelledby="user-menu-button"
        open={is_open}
        onClose={() => setIsOpen(false)}
        horizontalAlignment={MenuHorizontalAlignment.RIGHT}
      >
        <MobileMenuLabel>Forma Experience</MobileMenuLabel>
        {show_store && (
          <MobileMenuItem
            startText="Store"
            startIcon={StoreSvg}
            to={
              allowMarketplace(company)
                ? StoreRoutes.listExplore()
                : StoreRoutes.detailPretax({
                    pretax_account: 'home',
                  })
            }
          />
        )}
        <MobileMenuItem
          startText="Benefits"
          startIcon={WalletSvg}
          to={AccountRoutes.list()}
        />
        {show_claims && (
          <MobileMenuItem
            startText="Claims"
            startIcon={DocumentSvg}
            to={ClaimRoutes.list()}
          />
        )}
        <Divider />
        <MobileMenuLabel>Manage Your Profile</MobileMenuLabel>
        <MenuItem
          startText="Settings"
          startIcon={SettingSvg}
          to={SettingsRoutes.root()}
        />
        {show_store && (
          <MenuItem
            startText="History"
            startIcon={TimerSvg}
            to={HistoryRoutes.orderList()}
          />
        )}
        {hasPostTaxWallets && (
          <MenuItem
            startText="Program Policy"
            startIcon={DocumentsSvg}
            onClick={() => {
              window.open(reimbursement_policy_link);
              logEvent(EVENTS.ProgramPolicyViewed, {
                from_screen: 'profile_menu',
              });
            }}
          />
        )}
        <MenuItem
          startText="Help Center"
          startIcon={FloatSvg}
          onClick={() => {
            window.open(SupportRoutes.helpCenter());
            logEvent(EVENTS.HelpCenterViewed, {
              url: pathname,
            });
          }}
        />
        <MenuItem
          startText="Log out"
          startIcon={LogOutSvg}
          onClick={() => doLogOut()}
        />
      </Menu>
    </Container>
  );
};

export default TopNavUser;
